/**
 * Image proxy URL builder for AWS Serverless Image Handler / Dynamic Image Transformation.
 *
 * The handler accepts a base64-encoded JSON payload in the URL path:
 *   https://<proxy-host>/<base64({ bucket, key, edits })>
 *
 * Auto-WebP is enabled at the proxy level — the Lambda inspects the browser's
 * Accept header and serves WebP automatically. We only need to ask for resize.
 *
 * If `VITE_IMAGE_PROXY_URL` is empty, the original URL is returned unchanged
 * — components fall back to the original CDN link transparently.
 */

const PROXY_BASE = (import.meta.env.VITE_IMAGE_PROXY_URL || '').replace(/\/$/, '');

// S3 bucket containing the originals. Both CDN hostnames below resolve to this bucket.
const SOURCE_BUCKET = 'starprogpt';

// CloudFront hostnames that mirror the source bucket. URLs from these hosts are
// proxied; URLs from anywhere else (other CDNs, blob:, data:, etc.) pass through.
const CDN_HOSTS = new Set([
  'media.starproai.com',
  'd2i5ngpuayeib2.cloudfront.net',
]);

const SKIP_PATTERNS: RegExp[] = [
  /\.svg(\?|$)/i,
  /\.gif(\?|$)/i,
];

export function isImageProxyEnabled(): boolean {
  return PROXY_BASE.length > 0;
}

/**
 * Returns the S3 key for a CDN URL we recognise, or null if the URL is from
 * an unrelated origin (data:/blob:/external CDN/presigned S3) and should not
 * be proxied. Query strings and fragments are stripped — they aren't valid in
 * S3 keys and Image Handler ignores them.
 */
function extractS3Key(url: string): string | null {
  if (!url || /^(data|blob):/i.test(url)) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (!CDN_HOSTS.has(parsed.hostname)) return null;
  // Strip leading slash; pathname is already URL-encoded by the browser.
  return decodeURIComponent(parsed.pathname.replace(/^\//, ''));
}

interface ImageEdits {
  resize: {
    width: number;
    fit: 'inside' | 'cover' | 'contain' | 'fill' | 'outside';
    withoutEnlargement: boolean;
  };
  // `rotate: null` triggers Sharp's auto-orient based on EXIF — without this,
  // iPhone JPEGs (which encode rotation in EXIF, not pixels) come out sideways.
  rotate: null;
}

function encodePayload(bucket: string, key: string, edits: ImageEdits): string {
  const json = JSON.stringify({ bucket, key, edits });
  // btoa handles ASCII; JSON of bucket+key+edits is always ASCII for our payloads.
  return btoa(json);
}

/**
 * Build a proxy URL that returns the image resized to `width`. Auto-WebP at the
 * proxy decides the final format based on the browser's Accept header.
 *
 * Returns the original URL when:
 *  - the proxy is not configured,
 *  - the URL is SVG/GIF/data:/blob:,
 *  - the URL is from a host we don't proxy (external CDN, presigned S3, etc).
 */
export function buildOptimizedImageUrl(
  originalUrl: string,
  width: number,
  // Quality parameter retained for API compatibility; AWS Solution applies its
  // own per-format quality defaults and Auto-WebP, so we don't pass quality
  // edits per request to keep cache keys small.
  _quality = 85,
): string {
  if (!isImageProxyEnabled()) return originalUrl;
  if (SKIP_PATTERNS.some((re) => re.test(originalUrl))) return originalUrl;
  const key = extractS3Key(originalUrl);
  if (!key) return originalUrl;
  const payload = encodePayload(SOURCE_BUCKET, key, {
    resize: { width, fit: 'inside', withoutEnlargement: true },
    rotate: null,
  });
  return `${PROXY_BASE}/${payload}`;
}

export const RESPONSIVE_WIDTHS = [64, 160, 384, 640, 1280, 1920] as const;

/**
 * Build a `srcset` covering RESPONSIVE_WIDTHS. The browser picks the narrowest
 * variant that satisfies the layout `sizes` hint and the device DPR.
 */
export function buildSrcSet(originalUrl: string, quality = 85): string {
  if (!isImageProxyEnabled()) return '';
  if (SKIP_PATTERNS.some((re) => re.test(originalUrl))) return '';
  if (!extractS3Key(originalUrl)) return '';
  return RESPONSIVE_WIDTHS
    .map((w) => `${buildOptimizedImageUrl(originalUrl, w, quality)} ${w}w`)
    .join(', ');
}
